/* ═════════════════════════ the term ═════════════════════════

   Longterm planning: one row per week between now and the end of the horizon,
   graded by how much work actually lands in it, with the insights that follow
   from the arithmetic — what is already late, what has to start early to
   survive a heavy week, and which quiet stretch is worth spending.

   The syllabus intake lives here too. Pasted text is parsed the moment it
   arrives, while the text is still in memory; a PDF or a screenshot is kept as
   a reminder and says so, rather than being marked read by something that
   never opened it. */
import { LT_KINDS, ltBand, ltKind } from '../core/domain.js';
import { deadlineLoad, standingMultiple } from '../state/evidence.js';
import { COURSE_COLORS } from '../core/palette.js';
import { DAY_MS, clamp, fmtDay, fmtDurMin, fromIsoDay, isoDay, startOfWeek, uid } from '../core/time.js';
import { DESK } from '../platform/bridge.js';
import { LEDGER, PLAN, planWeek, recomputePlans, touchPlan } from '../state/plans.js';
import { S, courseColor, save } from '../state/store.js';
import { askText, closeModal, el, openModal, openModals, reducedMotion, toast } from './dom.js';
import { renderCourseSelects } from './tasks.js';
import { renderWeekly } from './weekly.js';
import { $ } from './dom.js';
import { courseName } from '../state/store.js';
import { fmtMinOfDay } from './format.js';
import StudyHubEngine from '../core/engine.js';
import { showTab } from './shell.js';


export function boardHorizonItems() {
  const out = [];
  S.deadlines.forEach((d) => {
    const load = deadlineLoad(d);
    out.push({
      id: `board-dl-${d.id}`, refId: d.id, board: true, kind: 'deadline',
      title: d.title, courseId: d.courseId, start: null, end: d.due,
      effortMin: Math.round(load.projLeft), protect: false, notes: d.notes || '',
    });
  });
  S.events.forEach((ev) => {
    if (typeof ev.start !== 'number') return;
    out.push({
      id: `board-ev-${ev.id}`, refId: ev.id, board: true, kind: 'event',
      title: ev.title, courseId: ev.courseId, start: ev.start, end: ev.end || ev.start,
      effortMin: 0, protect: false, notes: ev.notes || '',
    });
  });
  return out;
}

/** Everything on the horizon, own entries first, filtered by the group picker. */
export function horizonItems() {
  const all = S.longterm.entries.concat(boardHorizonItems());
  const f = S.longterm.filterCourse;
  return f ? all.filter((e) => e.courseId === f) : all;
}

/** An entry's own effort, priced the way everything else in the app is priced.

    It used to be the standing multiple and nothing else, which meant the
    horizon and the week grid could quote two different numbers for one essay —
    one of them measured, the other an average of unlike work. There is one
    price now, and this is the same call the planner itself makes. */
export function ltProjectedMin(e) {
  if (!e.effortMin) return 0;
  // board deadlines arrive already projected — deadlineLoad applied it
  if (e.board) return e.effortMin;
  return StudyHubEngine.price({ mode: 'work', title: e.title, courseId: e.courseId, estMin: e.effortMin }).min;
}

/** The weeks the horizon covers: from this Monday, as far as the picker says. */
export function horizonWeeks() {
  const first = startOfWeek(new Date()).getTime();
  const items = horizonItems();

  /* Stretch to cover anything that falls past the chosen window rather than
     silently hiding it — an entry you cannot see is an entry you forget. */
  const furthest = items.reduce((a, e) => Math.max(a, e.end || 0), 0);
  const wanted = S.longterm.weeks;
  const needed = furthest ? Math.ceil((furthest - first) / (7 * DAY_MS)) + 1 : 0;
  const count = clamp(Math.max(wanted, Math.min(needed, 104)), 1, 104);

  const isSpan = (e) => typeof e.start === 'number' && e.start < e.end - DAY_MS;

  /* A span belongs to every week it touches, but it is not done four times
     over. Ten hours of TF-ing across a month is two and a half hours a week,
     and counting the whole ten in each of them paints four weeks crunch on the
     strength of one obligation. So the hours are shared out across the weeks
     the span actually covers. */
  const weeksTouched = (e) => {
    if (!isSpan(e)) return 1;
    let n = 0;
    for (let i = 0; i < count; i++) {
      const s = first + i * 7 * DAY_MS;
      if (e.start < s + 7 * DAY_MS && e.end >= s) n++;
    }
    return Math.max(1, n);
  };

  const weeks = [];
  for (let i = 0; i < count; i++) {
    const start = first + i * 7 * DAY_MS;
    const end = start + 7 * DAY_MS;

    /* A thing belongs to the week it is due. Spans (trips, commitments) belong
       to every week they touch, so a three-week trip greys out three weeks. */
    const inWeek = items.filter((e) => (isSpan(e)
      ? e.start < end && e.end >= start
      : e.end >= start && e.end < end));

    const min = inWeek.reduce((a, e) => a + ltProjectedMin(e) / weeksTouched(e), 0);
    const hours = min / 60;
    const protectedBy = inWeek.filter((e) => e.protect);

    weeks.push({
      i, start, end, items: inWeek, hours,
      band: ltBand(hours),
      protectedBy,
      plan: planWeek(start),
      startsHere: S.longterm.entries.filter((e) => e.startBy >= start && e.startBy < end),
    });
  }
  return weeks;
}

/** Widest week in view, so the gauges are comparable to each other. */
export const peakHours = (weeks) => Math.max(6, ...weeks.map((w) => w.hours));

export function renderHorizon() {
  const host = $('ltHorizon');
  if (!host) return;
  host.textContent = '';

  const weeks = horizonWeeks();
  const nothing = !S.longterm.entries.length && !S.longterm.sources.length && !boardHorizonItems().length;
  $('ltEmpty').hidden = !nothing;
  host.hidden = nothing;
  if (nothing) return;

  const peak = peakHours(weeks);
  const thisWeek = startOfWeek(new Date()).getTime();

  weeks.forEach((w) => {
    /* The plan's grade wins when it has one — it knows what must be *worked*
       here, which the raw hours do not: an essay due Monday of week six is
       week five's problem, and only the backward pass knows that. The gauge
       bar stays local and gross either way, so a wide bar reading "steady" is
       the plan saying the week is heavy and the work has been spread. */
    const load = w.plan?.load || w.band.key;

    const row = el('div', 'lt-week');
    row.dataset.load = load;
    row.dataset.week = isoDay(w.start);
    if (w.start === thisWeek) row.classList.add('is-now');
    if (w.protectedBy.length) row.classList.add('is-protected');

    const when = el('div', 'lt-wk-when');
    when.appendChild(el('strong', null, fmtDay(w.start)));
    when.appendChild(el('span', 'lt-wk-span', `– ${fmtDay(w.end - DAY_MS)}`));
    when.appendChild(el('span', 'lt-wk-idx',
      w.start === thisWeek ? 'this week' : w.i === 1 ? 'next week' : `week ${w.i + 1}`));
    row.appendChild(when);

    const body = el('div', 'lt-wk-body');

    const gauge = el('div', 'lt-wk-gauge');
    const bar = el('div', 'lt-gauge-bar');
    const fill = el('i');
    fill.style.width = `${clamp((w.hours / peak) * 100, 0, 100)}%`;
    bar.appendChild(fill);
    gauge.appendChild(bar);
    gauge.appendChild(el('span', 'lt-gauge-num',
      w.hours ? `${w.hours < 10 ? w.hours.toFixed(1) : Math.round(w.hours)}h lands` : 'clear'));
    gauge.appendChild(el('span', 'lt-gauge-band', (w.plan?.load || w.band.label)));

    /* A band is a verdict, and a verdict nobody can check is only a mood. The
       drawer is the subtraction behind it. */
    const drawer = el('button', 'btn btn-ghost lt-wk-drawer', '▤');
    drawer.title = 'The hours this week has, and everything asking for them';
    drawer.addEventListener('click', () => openLtLedger(w));
    gauge.appendChild(drawer);

    body.appendChild(gauge);

    if (w.items.length || w.startsHere.length) {
      const chips = el('div', 'lt-wk-items');
      w.items.forEach((e) => chips.appendChild(ltChip(e, w)));
      w.startsHere.forEach((e) => chips.appendChild(ltStartChip(e)));
      body.appendChild(chips);
    }

    if (w.plan?.headline || w.plan?.note) {
      const note = el('div', 'lt-wk-note');
      if (w.plan.headline) note.appendChild(el('strong', null, w.plan.headline));
      if (w.plan.note) note.appendChild(el('span', null, w.plan.note));
      body.appendChild(note);
    }

    row.appendChild(body);
    host.appendChild(row);
  });
}

export function ltChip(e, w) {
  const k = ltKind(e.kind);
  const chip = el('button', `lt-chip is-${e.kind}`);
  chip.style.setProperty('--chip', courseColor(e.courseId));
  if (e.board) chip.classList.add('is-board');
  if (e.protect) chip.classList.add('is-protect');

  chip.appendChild(el('span', 'lt-chip-glyph', k.glyph));
  chip.appendChild(el('span', 'lt-chip-title', e.title));

  const bits = [];
  if (e.kind === 'deadline') bits.push(fmtDay(e.end));
  else if (typeof e.start === 'number' && e.start < e.end - DAY_MS) bits.push(`${fmtDay(e.start)}–${fmtDay(e.end)}`);
  else bits.push(fmtDay(e.end));
  const proj = ltProjectedMin(e);
  if (proj) bits.push(fmtDurMin(proj));
  chip.appendChild(el('span', 'lt-chip-meta', bits.join(' · ')));

  chip.title = e.board
    ? `${e.title} — lives in studyHub's own lists. Edit it there.`
    : `${k.label} · ${courseName(e.courseId)} — click to edit`;

  if (e.board) chip.addEventListener('click', () => { showTab('calendar'); });
  else chip.addEventListener('click', () => openLtEntry(e.id));
  return chip;
}

/** The hollow twin of a chip: the last day this can begin and still finish. */
export function ltStartChip(e) {
  const chip = el('button', 'lt-chip is-start');
  chip.style.setProperty('--chip', courseColor(e.courseId));
  chip.appendChild(el('span', 'lt-chip-glyph', '▷'));
  chip.appendChild(el('span', 'lt-chip-title', `start: ${e.title}`));
  chip.appendChild(el('span', 'lt-chip-meta', `due ${fmtDay(e.end)}`));
  chip.title = 'The planner worked out that this has to begin here. Click to open the entry.';
  chip.addEventListener('click', () => openLtEntry(e.id));
  return chip;
}

/* ── the week drawer ──

   One row of the horizon, opened up. The band above it is a judgement and the
   headline is a sentence; this is the arithmetic both of them rest on, which
   is the only thing that makes either of them worth believing.

   It is deliberately built out of the ledger rather than out of PLAN: PLAN is
   what the engine decided, the ledger is what it decided it from, and a drawer
   that quoted the conclusion back at you would be showing its working in the
   same hand that wrote the answer. */

export function openLtLedger(w) {
  if (!$('ltLedgerModal')) return;

  const days = ((LEDGER && LEDGER.days) || []).filter((d) => d.dayMs >= w.start && d.dayMs < w.end);
  const dayIdx = new Set(days.map((d) => d.i));
  const envelope = days.length * Math.max(0, S.weekly.dayEnd - S.weekly.dayStart);
  const freeMin = days.reduce((a, d) => a + (d.freeMin || 0), 0);

  $('ltLedgerTitle').textContent = `The week of ${fmtDay(w.start)}`;
  $('ltLedgerHeadline').textContent = w.plan?.headline
    || (w.hours ? `${fmtDurMin(Math.round(w.hours * 60))} lands here.` : 'Nothing lands here.');

  /* Supply, day by day, because that is the grain at which it can be checked:
     every one of these numbers is a column of the week grid. */
  const supply = $('ltLedgerSupply');
  supply.textContent = '';
  supply.appendChild(ledgerRow({
    title: `the window you set — ${fmtMinOfDay(S.weekly.dayStart)} to ${fmtMinOfDay(S.weekly.dayEnd)}`,
    num: fmtDurMin(envelope),
    basis: `${days.length} day${days.length === 1 ? '' : 's'}`,
  }));
  days.forEach((d) => {
    const booked = Math.max(0, Math.max(0, S.weekly.dayEnd - S.weekly.dayStart) - (d.freeMin || 0));
    supply.appendChild(ledgerRow({
      title: fmtDay(d.dayMs),
      num: `${fmtDurMin(d.freeMin || 0)} free`,
      basis: d.isProtected ? `away — ${d.protectedBy || 'protected'}` : booked ? `${fmtDurMin(booked)} spoken for` : 'nothing on it',
    }));
  });
  if (!days.length) supply.appendChild(el('div', 'empty-list', 'This week is past the end of the ledger.'));

  /* Demand, one row per thing the backward pass put here. A merge is printed
     under the thing that absorbed it: work the user cannot see counted is work
     she will read as lost. */
  const demand = $('ltLedgerDemand');
  demand.textContent = '';
  let committed = 0;
  const rows = ((LEDGER && LEDGER.demands) || [])
    .map((d) => ({ d, min: (d.claims || []).filter((c) => dayIdx.has(c.dayIdx)).reduce((a, c) => a + c.min, 0) }))
    .filter((r) => r.min > 0)
    .sort((a, b) => b.min - a.min || String(a.d.key).localeCompare(String(b.d.key)));

  rows.forEach(({ d, min }) => {
    committed += min;
    demand.appendChild(ledgerRow({
      title: d.title || '(untitled)',
      num: min === d.min ? fmtDurMin(min) : `${fmtDurMin(min)} of ${fmtDurMin(d.min)}`,
      basis: d.basis || '',
      rung: d.rung || null,
    }));
    (d.merged || []).forEach((m) => {
      demand.appendChild(el('div', 'lt-lg-merge', `↳ absorbs ${m.title}${m.why ? ` — ${m.why}` : ''}`));
    });
  });
  if (!rows.length) demand.appendChild(el('div', 'empty-list', 'Nothing has to be worked in this week.'));

  const over = committed - freeMin;
  const balance = $('ltLedgerBalance');
  balance.className = 'lt-lg-balance ' + (over > 0 ? 'is-over' : 'is-clear');
  balance.textContent = over > 0
    ? `${fmtDurMin(committed)} asked of ${fmtDurMin(freeMin)} — ${fmtDurMin(over)} over`
    : `${fmtDurMin(committed)} asked of ${fmtDurMin(freeMin)} — ${fmtDurMin(-over)} spare`;

  $('ltLedgerClears').textContent = w.plan?.note || '';
  openModal('ltLedgerModal');
}

/** One line of the drawer: what it is, what it costs, and on what evidence. */
/** One line of the week drawer. `rung` is only carried by the rows that price
 *  something; the supply rows are subtraction and have no evidence behind them.
 *  @param {{title: string, num: string, basis: string, rung?: string}} row */
export function ledgerRow({ title, num, basis, rung }) {
  const row = el('div', 'lt-lg-row');
  if (rung) {
    const swatch = el('span', 'lt-lg-rung');
    swatch.dataset.rung = rung;
    row.appendChild(swatch);
  }
  row.appendChild(el('span', 'lt-lg-title', title));
  row.appendChild(el('span', 'lt-lg-num', num));
  if (basis) {
    const b = el('span', 'lt-lg-basis', basis);
    if (rung) b.dataset.rung = rung;
    row.appendChild(b);
  }
  return row;
}

export const fmtBytes = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/* ─────────────────── source material, for both planners ───────────────────

   A term arrives in whatever form the department happened to publish it: a
   PDF, a page of text on a course site, or — most often, in practice — a
   screenshot of a timetable somebody posted as an image. All three are
   accepted, because refusing two of them only moves the work onto the user.

   Only one of them can be read, and the app is careful to say so: the planner
   reads pasted text; a PDF or a screenshot is kept as a reminder. The text
   passes through this file on its way to disk and its dates come out of it at
   that moment, which is the only moment it exists inside the app at all.

   They are files on disk, never localStorage: a syllabus PDF would exhaust
   the quota by itself and a screenshot is worse. The renderer keeps only the
   metadata.

   Both planners share this, scoped by which one is asking, because "what did
   I feed it" should mean the same thing on either tab. */

export const SRC_SCOPES = {
  longterm: { dir: 'data/syllabi',     state: () => S.longterm, redraw: () => renderLongterm(), noun: 'syllabus' },
  weekly:   { dir: 'data/weekly-refs', state: () => S.weekly,   redraw: () => renderWeekly(),   noun: 'reference' },
};

export const SRC_TYPES = [
  { type: 'pdf',   glyph: '🗎', test: (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name) },
  { type: 'image', glyph: '🖼', test: (f) => /^image\/(png|jpeg|webp|gif)$/.test(f.type) || /\.(png|jpe?g|webp|gif)$/i.test(f.name) },
  { type: 'text',  glyph: '❝', test: (f) => f.type === 'text/plain' || /\.(txt|md)$/i.test(f.name) },
];

export const srcTypeOf = (f) => (SRC_TYPES.find((t) => t.test(f)) || {}).type || null;
export const srcGlyph = (type) => (SRC_TYPES.find((t) => t.type === type) || SRC_TYPES[0]).glyph;

/** Drop or paste files into one planner's pile. */
async function addSourceFiles(scope, files) {
  const cfg = SRC_SCOPES[scope];
  const typed = [...files].map((f) => ({ f, type: srcTypeOf(f) })).filter((x) => x.f && x.type);

  if (!typed.length) {
    toast('That has to be a PDF, an image of a calendar, or a text file.', true);
    return 0;
  }
  if (!DESK || !DESK.addSource) {
    toast('Attaching files needs the desktop app — a browser tab cannot write to disk.', true);
    return 0;
  }

  let added = 0;
  for (const { f, type } of typed) {
    try {
      const bytes = new Uint8Array(await f.arrayBuffer());
      const r = await DESK.addSource(scope, f.name, bytes);
      if (!r || !r.ok) { toast(r?.message || `Could not store ${f.name}.`, true); continue; }
      cfg.state().sources.push({
        id: uid(), name: f.name, file: r.file, type, bytes: r.bytes, addedAt: Date.now(), read: false,
      });
      added++;
    } catch (err) {
      toast(`Could not read ${f.name}: ${/** @type {Error} */ (err).message}`, true);
    }
  }

  if (added) {
    save();
    cfg.redraw();
    const what = added > 1 ? `${added} files` : typed[0].type === 'image' ? 'Screenshot' : `That ${typed[0].type === 'pdf' ? 'PDF' : 'file'}`;
    toast(`${what} attached — kept as a reminder of the ${scope === 'weekly' ? 'times' : 'dates'} still to type in.`);
  }
  return added;
}

/** Keep a block of pasted text as a source in its own right.

    And, on the horizon, read it — here, while the whole text is still in
    memory on its way to disk. There is no way back to it afterwards: the file
    API can add, remove and open a source but never hand its contents back, and
    a syllabus is far too big to keep a second copy of in localStorage. So the
    one moment the text exists inside the app is this one, and the dates come
    out of it now or never. */
async function addSourceText(scope, name, text) {
  const cfg = SRC_SCOPES[scope];
  if (!text || !text.trim()) { toast('There was nothing in that paste.', true); return false; }
  if (!DESK || !DESK.addSourceText) {
    toast('Keeping pasted text needs the desktop app — a browser tab cannot write to disk.', true);
    return false;
  }

  const label = (name || '').trim() || 'Pasted text';
  const r = await DESK.addSourceText(scope, label, text);
  if (!r || !r.ok) { toast(r?.message || 'Could not keep that text.', true); return false; }

  const row = {
    id: uid(), name: label, file: r.file, type: 'text', bytes: r.bytes,
    addedAt: Date.now(), read: false,
    // a couple of lines, so the row can show what it is without opening it
    preview: text.trim().replace(/\s+/g, ' ').slice(0, 120),
  };
  cfg.state().sources.push(row);

  let found = [];
  if (scope === 'longterm') {
    found = StudyHubEngine.extractFromText(text, {
      sourceId: row.id, courses: S.courses, now: Date.now(),
    });
    S.longterm.textFinds.push({ sourceId: row.id, at: Date.now(), items: found });
    /* `read` means read, and for pasted text it now is one. It is set here and
       nowhere else, which is what stops a PDF from ever wearing the word. */
    row.read = true;
    row.found = found.length;
  }

  touchPlan();
  save();
  cfg.redraw();
  toast(scope === 'longterm'
    ? `Kept ${fmtBytes(r.bytes)} of text — ${found.length} date${found.length === 1 ? '' : 's'} pulled out of it.`
    : `Kept ${fmtBytes(r.bytes)} of text.`);
  return true;
}

export function removeSource(scope, id) {
  const cfg = SRC_SCOPES[scope];
  const list = cfg.state().sources;
  const src = list.find((s) => s.id === id);
  if (!src) return;
  if (DESK && DESK.removeSource) DESK.removeSource(scope, src.file);
  cfg.state().sources = list.filter((s) => s.id !== id);
  // what was read out of it goes with it; what was kept from those dates does not
  S.longterm.textFinds = S.longterm.textFinds.filter((f) => f.sourceId !== id);
  touchPlan();
  save();
  cfg.redraw();
  toast('Removed. Anything already kept from it stays.');
}

/** The attached-things list, drawn the same way on either tab. */
export function renderSourceList(scope, hostId, emptyText) {
  const host = $(hostId);
  if (!host) return;
  host.textContent = '';

  const list = SRC_SCOPES[scope].state().sources;
  if (!list.length) {
    host.appendChild(el('div', 'empty-list', DESK
      ? emptyText
      : 'Attachments need the desktop app. Everything else on this tab works in the browser.'));
    return;
  }

  list.forEach((s) => {
    /* Only pasted text on the horizon is ever read, so only that may look
       read. A PDF, a screenshot, and anything pasted at the week — where what
       is wanted is a time of day rather than a date — is a file on disk and a
       note to yourself, and saying anything else about it would be the most
       damaging kind of wrong: confident, instant, and about something you
       could have checked. */
    const parsed = scope === 'longterm' && s.type === 'text';
    const status = parsed
      ? (s.read ? `${s.found || 0} date${s.found === 1 ? '' : 's'} pulled out` : 'nothing dated in it')
      : `kept as a reminder — type the ${scope === 'weekly' ? 'times' : 'dates'} in yourself`;

    const row = el('div', 'lt-file' + (parsed && s.read ? ' is-read' : ''));
    row.appendChild(el('span', 'lt-file-glyph', srcGlyph(s.type)));

    const main = el('div', 'lt-file-main');
    main.appendChild(el('div', 'lt-file-name', s.name));

    const kind = s.type === 'image' ? 'screenshot' : s.type === 'text' ? 'pasted text' : 'PDF';
    main.appendChild(el('div', 'lt-file-meta', `${kind} · ${fmtBytes(s.bytes || 0)} · ${status}`));
    if (s.preview) main.appendChild(el('div', 'lt-file-preview', `“${s.preview}…”`));
    row.appendChild(main);

    if (DESK && DESK.openSource) {
      const open = el('button', 'icon-btn', '↗');
      open.title = 'Open it';
      open.addEventListener('click', () => DESK.openSource(scope, s.file));
      row.appendChild(open);
    }

    const del = el('button', 'icon-btn', '🗑');
    del.title = 'Remove this';
    del.addEventListener('click', () => removeSource(scope, s.id));
    row.appendChild(del);

    host.appendChild(row);
  });
}

/* ── the paste path ──
   Cmd+Shift+4 then Cmd+V is how a calendar screenshot actually gets from the
   screen into an app, so paste is a first-class intake here rather than a
   convenience on top of the file picker. Pasted text opens the modal instead
   of saving silently: a stray Cmd+V should not quietly create a file. */

export let pasteScope = null;

export function openPasteText(scope, prefill = '') {
  pasteScope = scope;
  $('pasteName').value = '';
  $('pasteBody').value = prefill;
  $('pasteHint').textContent = scope === 'longterm'
    ? 'Paste a syllabus, a course page, a reading list — whatever states the dates. This one is read here and now.'
    : 'Paste a timetable, a club schedule, an email about section times — whatever says when things happen.';
  openModal('pasteModal');
  setTimeout(() => $(prefill ? 'pasteName' : 'pasteBody').focus(), 30);
}

export async function confirmPasteText() {
  const ok = await addSourceText(pasteScope, $('pasteName').value, $('pasteBody').value);
  if (ok) closeModal('pasteModal');
}

/** Wire drop, click-to-choose and paste for one planner's intake zone. */
export function initSourceIntake(scope, opts) {
  const zone = $(opts.zoneId);
  const input = $(opts.inputId);
  const panel = $(opts.panelId);
  if (!zone || !panel) return;

  input.addEventListener('change', (e) => {
    addSourceFiles(scope, e.target.files);
    e.target.value = '';
  });

  ['dragenter', 'dragover'].forEach((ev) => zone.addEventListener(ev, (e) => {
    e.preventDefault();
    zone.classList.add('is-over');
  }));
  ['dragleave', 'drop'].forEach((ev) => zone.addEventListener(ev, (e) => {
    e.preventDefault();
    if (ev === 'dragleave' && zone.contains(e.relatedTarget)) return;
    zone.classList.remove('is-over');
  }));
  zone.addEventListener('drop', (e) => addSourceFiles(scope, e.dataTransfer?.files || []));

  /* A file dropped anywhere on the tab means the same thing as one dropped on
     the zone — and the browser's own "open this file" default must not win. */
  panel.addEventListener('dragover', (e) => e.preventDefault());
  panel.addEventListener('drop', (e) => {
    if (e.target.closest(`#${opts.zoneId}`)) return;
    e.preventDefault();
    if (e.dataTransfer?.files?.length) addSourceFiles(scope, e.dataTransfer.files);
  });

  if (opts.pasteBtnId) $(opts.pasteBtnId).addEventListener('click', () => openPasteText(scope));
}

/* ── ⌘V, wherever the caret happens to be ──

   Paste has to be caught on the document, not on the tab it belongs to. A
   paste event is dispatched to whatever holds focus and then bubbles *up*;
   a <div> is not focusable, so with nothing selected the event fires at
   <body> — an ancestor of the tab, which therefore never sees it. Listening
   on the panel meant ⌘V did nothing at all unless you had first clicked into
   a field inside it.

   So: one listener at the top, which works out for itself which planner is
   on screen. */

export const PASTE_TABS = { longterm: 'longterm', weekly: 'weekly' };

export function initPasteAnywhere() {
  document.addEventListener('paste', (e) => {
    const scope = PASTE_TABS[S.ui.tab];
    if (!scope) return;

    // typing into a field, or into the keep-this-text box itself: ⌘V is theirs
    const t = /** @type {any} */ (e.target);
    if (t && t.closest && t.closest('input, textarea, [contenteditable]')) return;
    // a modal is a conversation of its own; do not reach around it
    if (openModals.length) return;

    const files = [...(e.clipboardData?.files || [])];
    if (files.length) {
      e.preventDefault();
      addSourceFiles(scope, files);
      return;
    }

    /* Long enough to be a syllabus rather than a stray copy. Short pastes fall
       through untouched, so ⌘V keeps meaning what it always did. */
    const text = e.clipboardData?.getData('text/plain') || '';
    if (text.trim().length > 40) {
      e.preventDefault();
      openPasteText(scope, text);
    }
  });
}

export let ltEditId = null;
export let ltKindNow = 'deadline';

export function setLtKind(kind) {
  ltKindNow = kind;
  [...$('ltKindSeg').children].forEach((b) => b.classList.toggle('is-active', b.dataset.kind === kind));
  $('ltKindExplainer').textContent = ltKind(kind).explain;

  const isDeadline = kind === 'deadline';
  const spans = kind === 'trip' || kind === 'commitment';

  $('ltStartField').hidden = isDeadline;
  $('ltStartLabel').textContent = 'Starts';
  $('ltEndLabel').textContent = isDeadline ? 'Due' : 'Ends';

  // A trip has no workload; an event's cost is the hours it eats, not homework
  $('ltEffortBox').hidden = kind === 'trip';
  $('ltProtectRow').hidden = isDeadline;
  $('ltProtectLabel').textContent = spans
    ? 'Protect this time — no work should be planned into it'
    : 'Protect this time — keep the day around it clear';

  updateLtMeta();
}

export function ltEffortMin() { return (+$('ltEffortH').value || 0) * 60 + (+$('ltEffortM').value || 0); }

export function updateLtMeta() {
  const min = ltEffortMin();
  const m = standingMultiple();
  const bits = [];
  if (min && m) bits.push(`At your standing multiple of ${m.toFixed(2)}×, plan for ${fmtDurMin(Math.round(min * m))}.`);
  else if (min) bits.push(`${fmtDurMin(min)} of work. Once you have finished a few tasks, your multiple gets applied here.`);
  if (ltKindNow === 'trip') bits.push('Trips carry no workload — they take capacity away from the week instead.');
  $('ltMeta').textContent = bits.join(' ');
}

export function openLtEntry(id) {
  const e = id ? S.longterm.entries.find((x) => x.id === id) : null;
  ltEditId = e ? e.id : null;

  $('ltModalTitle').textContent = e ? 'Edit this entry' : 'Add to the horizon';
  $('ltSave').textContent = e ? 'Save changes' : 'Add it';
  $('ltDelete').hidden = !e;

  const sel = $('ltCourse');
  sel.textContent = '';
  S.courses.forEach((c) => sel.appendChild(new Option(c.name, c.id)));
  sel.value = (e && e.courseId) || S.longterm.filterCourse || (S.courses[0] || {}).id || '';

  $('ltTitle').value = e ? e.title : '';
  $('ltNotes').value = e ? (e.notes || '') : '';
  $('ltProtect').checked = e ? !!e.protect : false;

  const eff = e ? e.effortMin || 0 : 0;
  $('ltEffortH').value = Math.floor(eff / 60);
  $('ltEffortM').value = eff % 60;

  // default a new entry to a fortnight out — far enough to be planning, not doing
  const end = e ? e.end : Date.now() + 14 * DAY_MS;
  $('ltEnd').value = isoDay(end);
  $('ltStart').value = isoDay(e && typeof e.start === 'number' ? e.start : end);

  setLtKind(e ? e.kind : 'deadline');
  openModal('ltModal');
  setTimeout(() => $('ltTitle').focus(), 30);
}

export function saveLtEntry() {
  const title = $('ltTitle').value.trim();
  if (!title) { toast('Give it a name — a blank row on the horizon says nothing.', true); return; }

  const end = fromIsoDay($('ltEnd').value);
  if (!end) { toast(ltKindNow === 'deadline' ? 'A deadline needs a due date.' : 'This needs an end date.', true); return; }

  /* A deadline is a moment, so it lands at the end of its day; anything else is
     a span and starts at the beginning of one. */
  const endMs = end + (24 * 3600e3 - 60000);
  let start = ltKindNow === 'deadline' ? null : fromIsoDay($('ltStart').value);
  if (start && start > endMs) { toast('That ends before it starts.', true); return; }

  const patch = {
    kind: ltKindNow,
    title,
    courseId: $('ltCourse').value,
    start,
    end: endMs,
    effortMin: ltKindNow === 'trip' ? 0 : ltEffortMin(),
    protect: ltKindNow === 'deadline' ? false : $('ltProtect').checked,
    notes: $('ltNotes').value.trim(),
  };

  const existing = ltEditId && S.longterm.entries.find((x) => x.id === ltEditId);
  if (existing) {
    Object.assign(existing, patch);
    toast('Entry updated.');
  } else {
    S.longterm.entries.push({ id: uid(), created: Date.now(), source: 'manual', startBy: null, ...patch });
    toast('On the horizon.');
  }

  touchPlan();
  save();
  closeModal('ltModal');
  renderLongterm();
}

export function deleteLtEntry() {
  if (!ltEditId) return;
  S.longterm.entries = S.longterm.entries.filter((x) => x.id !== ltEditId);
  touchPlan();
  save();
  closeModal('ltModal');
  renderLongterm();
  toast('Removed from the horizon.');
}

export function renderInsights() {
  const host = $('ltInsights');
  if (!host) return;
  host.textContent = '';

  const badge = $('ltInsightCount');
  badge.textContent = String(PLAN.insights.length);
  badge.classList.toggle('is-zero', !PLAN.insights.length);

  if (!PLAN.insights.length) {
    /* Two quite different silences, and they must not read alike: a term that
       was weighed and came out clean, and a term with nothing in it yet. */
    const anything = S.longterm.entries.length || S.longterm.sources.length || boardHorizonItems().length;
    host.appendChild(el('div', 'empty-list', PLAN.status === 'error'
      ? (PLAN.message || 'The count hit a problem.')
      : anything
        ? 'The term was weighed and nothing is worth flagging. That is a good sign.'
        : 'Nothing on the horizon yet. Add a deadline or a trip and this fills in as you type.'));
    return;
  }

  PLAN.insights.forEach((ins) => {
    const n = el('div', `sugg-item lt-insight is-${ins.kind || 'note'}`);
    n.appendChild(el('div', 'lt-ins-kind', ins.kind || 'note'));
    n.appendChild(el('div', 'sg-title', ins.title));
    if (ins.body) n.appendChild(el('div', 'lt-ins-body', ins.body));

    const week = ins.week ? fromIsoDay(ins.week) : null;
    const meta = [];
    if (week) meta.push(`week of ${fmtDay(week)}`);
    if (meta.length) n.appendChild(el('div', 'sg-meta', meta.join(' · ')));

    const acts = el('div', 'sg-actions');

    const a = ins.action;
    if (a && a.type === 'start-by' && a.entryId && a.startBy) {
      const target = S.longterm.entries.find((x) => x.id === a.entryId);
      if (target) {
        const startBy = fromIsoDay(a.startBy);
        const set = el('button', 'btn btn-primary', target.startBy === startBy ? '✓ On the horizon' : (a.label || `Start by ${fmtDay(startBy)}`));
        set.disabled = target.startBy === startBy;
        set.addEventListener('click', () => {
          target.startBy = startBy;
          /* Accepting a start-by is not just a mark on the horizon: it is a
             ruling the next pass has to obey, so nothing for this entry is
             ever placed before that day again. */
          touchPlan();
          save();
          renderLongterm();
          toast(`Marked: begin “${target.title}” the week of ${fmtDay(startOfWeek(new Date(startBy)).getTime())}.`);
        });
        acts.appendChild(set);
      }
    }

    if (week) {
      const go = el('button', 'btn', 'Show the week');
      go.addEventListener('click', () => flashWeek(week));
      acts.appendChild(go);
    }

    if (acts.children.length) n.appendChild(acts);
    host.appendChild(n);
  });
}

/** Scroll a week into view and mark it, so an insight can point at something. */
export function flashWeek(ms) {
  const key = isoDay(startOfWeek(new Date(ms)).getTime());
  const row = document.querySelector(`.lt-week[data-week="${key}"]`);
  if (!row) return;
  row.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
  row.classList.remove('is-flash');
  void /** @type {HTMLElement} */ (row).offsetWidth;   // restart the animation on a repeat click
  row.classList.add('is-flash');
  setTimeout(() => row.classList.remove('is-flash'), 1600);
}

/** Syllabus dates not yet ruled on. */
export function liveExtracted() {
  const seen = S.longterm.seen.kept.concat(S.longterm.seen.dismissed);
  return PLAN.extracted.filter((x) => !seen.includes(x.id));
}

export function renderExtracted() {
  const host = $('ltExtracted');
  if (!host) return;
  host.textContent = '';

  const list = liveExtracted();
  const badge = $('ltExtractedCount');
  badge.textContent = String(list.length);
  badge.classList.toggle('is-zero', !list.length);

  if (!list.length) {
    /* Three silences again, and only one of them means "nothing to find": a
       pile of screenshots is not a pile the parser has been through. */
    const anyText = S.longterm.sources.some((s) => s.type === 'text');
    host.appendChild(el('div', 'empty-list', !S.longterm.sources.length
      ? 'Paste syllabus text above and the dates in it land here. A PDF or a screenshot is kept as a reminder — those you type in yourself.'
      : anyText
        ? 'Everything pulled out of your pasted text has been dealt with.'
        : 'Paste your syllabus text and the dates in it land here.'));
    return;
  }

  list.forEach((x) => {
    const n = el('div', 'sugg-item');
    n.appendChild(el('div', 'sg-title', x.title || '(untitled)'));

    const due = fromIsoDay(x.due);
    const meta = [];
    if (due) meta.push(`due ${fmtDay(due)}`);
    if (x.courseHint) meta.push(x.courseHint);
    if (x.estMin) meta.push(`suggested ${fmtDurMin(x.estMin)}`);
    n.appendChild(el('div', 'sg-meta', meta.join(' · ')));
    if (x.quote) n.appendChild(el('div', 'sg-why', `“${x.quote}”`));

    const acts = el('div', 'sg-actions');

    const keep = el('button', 'btn btn-primary', 'Keep');
    keep.addEventListener('click', () => keepExtracted(x));
    acts.appendChild(keep);

    const edit = el('button', 'btn', 'Keep & edit');
    edit.addEventListener('click', () => { const id = keepExtracted(x, true); if (id) openLtEntry(id); });
    acts.appendChild(edit);

    const no = el('button', 'btn btn-ghost', 'Dismiss');
    no.addEventListener('click', () => {
      S.longterm.seen.dismissed.push(x.id);
      save();
      renderLongterm();
    });
    acts.appendChild(no);

    n.appendChild(acts);
    if (x.confidence) n.appendChild(el('div', 'sugg-conf', `confidence: ${x.confidence}`));
    host.appendChild(n);
  });
}

/** Turn one syllabus date into a real entry. Returns its id. */
export function keepExtracted(x, quiet = false) {
  const due = fromIsoDay(x.due);
  if (!due) { toast('That one has no readable date.', true); return null; }

  const hinted = x.courseHint && S.courses.find((c) =>
    c.name.toLowerCase().includes(String(x.courseHint).toLowerCase())
    || String(x.courseHint).toLowerCase().includes(c.name.toLowerCase()));

  const entry = {
    id: uid(),
    created: Date.now(),
    source: `syllabus:${x.sourceId || ''}`,
    kind: LT_KINDS.some((k) => k.key === x.kind) ? x.kind : 'deadline',
    title: x.title || 'Untitled',
    courseId: (hinted || S.courses[0] || {}).id || '',
    start: null,
    end: due + (24 * 3600e3 - 60000),
    effortMin: Math.max(0, +x.estMin || 0),
    protect: false,
    notes: x.quote ? `From the syllabus: “${x.quote}”` : '',
    startBy: null,
  };

  S.longterm.entries.push(entry);
  S.longterm.seen.kept.push(x.id);
  touchPlan();
  save();
  renderLongterm();
  if (!quiet) toast(`“${entry.title}” is on the horizon.`);
  return entry.id;
}

/* ─────────────────── what the count says about itself ───────────────────

   Nothing here takes a minute any more, so nothing here may say it is working.
   There is no busy state to render and no age to report: the answer on screen
   was worked out on the way into this render, and the only honest thing the
   line can say is how much of it there is. */

export function renderPlanStatus() {
  const dot = $('planDot');
  if (!dot) return;

  $('ltCalcLabel').textContent = 'Recount the term';

  const when = $('planWhen');
  $('planHeadline').textContent = 'Longterm view';

  dot.className = 'sync-dot';
  if (PLAN.status === 'error') {
    dot.classList.add('is-bad');
    when.textContent = (PLAN.message || 'the count hit a problem').slice(0, 80);
    return;
  }

  dot.classList.add('is-ok');
  when.textContent = PLAN.weeks.length
    ? `${PLAN.weeks.length} weeks, worked out just now`
    : 'nothing on the horizon yet';
}

export function renderLongterm() {
  if (!$('ltHorizon')) return;
  recomputePlans();
  renderPlanStatus();
  renderSourceList('longterm', 'syllabusList',
    'Nothing attached yet. Pasted text is read here and now; a PDF or a screenshot is kept on your machine as a reminder of dates still to type in.');
  renderHorizon();
  renderInsights();
  renderExtracted();
}

/** The group picker, kept in step with the courses that exist. */
export function renderLtGroupFilter() {
  const sel = $('ltGroupFilter');
  if (!sel) return;
  const current = S.longterm.filterCourse;
  sel.textContent = '';
  sel.appendChild(new Option('All groups', ''));
  S.courses.forEach((c) => sel.appendChild(new Option(c.name, c.id)));
  sel.value = S.courses.some((c) => c.id === current) ? current : '';
  S.longterm.filterCourse = sel.value;
}

export function initLongtermTab() {
  $('ltAdd').addEventListener('click', () => openLtEntry(null));
  /* The button survives the skill that used to be behind it. Instant is not a
     reason to take away the user's sense that she can ask. */
  $('ltCalc').addEventListener('click', () => {
    recomputePlans(true);
    renderLongterm();
    toast('Recounted.');
  });
  $('ltSave').addEventListener('click', saveLtEntry);
  $('ltDelete').addEventListener('click', deleteLtEntry);

  [...$('ltKindSeg').children].forEach((b) =>
    b.addEventListener('click', () => setLtKind(b.dataset.kind)));

  ['ltEffortH', 'ltEffortM'].forEach((id) => $(id).addEventListener('input', updateLtMeta));

  $('ltAddCourse').addEventListener('click', () => {
    askText('Add group', 'Group name', '', (name) => {
      const c = { id: uid(), name, color: COURSE_COLORS[S.courses.length % COURSE_COLORS.length] };
      S.courses.push(c);
      save();
      renderCourseSelects();
      renderLtGroupFilter();
      const sel = $('ltCourse');
      sel.textContent = '';
      S.courses.forEach((x) => sel.appendChild(new Option(x.name, x.id)));
      sel.value = c.id;
    });
  });

  $('ltGroupFilter').addEventListener('change', (e) => {
    S.longterm.filterCourse = e.target.value;
    save();
    renderHorizon();
  });

  [...$('ltHorizonSeg').children].forEach((b) => b.addEventListener('click', () => {
    S.longterm.weeks = +b.dataset.weeks;
    [...$('ltHorizonSeg').children].forEach((x) => x.classList.toggle('is-active', x === b));
    // how far out the horizon reaches is an input to the count, not a view
    touchPlan();
    save();
    renderLongterm();
  }));
  [...$('ltHorizonSeg').children].forEach((b) =>
    b.classList.toggle('is-active', +b.dataset.weeks === S.longterm.weeks));

  initSourceIntake('longterm', {
    zoneId: 'syllabusZone', inputId: 'syllabusFile',
    panelId: 'tab-longterm', pasteBtnId: 'ltPasteText',
  });

  renderLtGroupFilter();
}

/* ═════════════════════ 6. WEEKLY PLANNING ═════════════════════
   The horizon says which week will hurt. This tab is the week itself.

   A week is made of three quite different things, and the whole tab turns on
   telling them apart:

     · things that happen at a time and you attend — lectures, sections, office
       hours, club meetings, shifts. They are pinned. Nothing gets planned on
       top of them.
     · things that come due and take work — problem sets, readings, discussion
       posts. The due moment is fixed; *when you do them* is the open question,
       and answering it well is the point of the tab.
     · things that want to happen often and do not care when — the gym. They
       fill whatever is left, spread out rather than clustered.

   Most of them repeat, which is what separates this from the term view: you
   describe your week once, and it is your week every week.

   The estimate problem is handled with the app's own history rather than a
   guess. studyHub already knows what your problem sets really take, because it
   has watched you finish a dozen of them. That is what `historyStats()` mines,
   so the plan can argue with your estimate instead of trusting it.           */




/** "14:30" → 870. The plan file and every stored time speak this. */
/** 870 → "2:30 pm", in whatever the clock is set to. */

/* ─────────────────── what your history actually says ───────────────────

   "Problem set 4" and "PSet 7" are the same kind of work, and the only way to
   learn anything from finishing one is to recognise the other as its sibling.
   So a title is reduced to its shape: known synonyms collapsed to one word,
   digits and ordinals thrown away, and whatever survives is the key. */
